import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export interface SeriesColumn<T> {
  header: string;
  cell: (row: T) => React.ReactNode;
  align?: 'left' | 'right';
  /** Definition tooltip on the header (dotted underline). */
  headerTip?: string;
}

interface SeriesTableProps<T> {
  columns: SeriesColumn<T>[];
  rows: T[];
  emptyText?: string;
  /** Bounded, sticky-header scroll box (e.g. `max-h-60`). */
  maxHeightClass?: string;
}

/**
 * Generic data-table twin for the trend charts (86d3peur0 Phase C) — the accessible relief channel every
 * chart must have. Right-aligned columns use tabular numerics; headers can carry a definition tooltip.
 */
export function SeriesTable<T>({ columns, rows, emptyText = 'No data', maxHeightClass }: SeriesTableProps<T>) {
  return (
    <Table containerClassName={maxHeightClass ? cn('overflow-auto', maxHeightClass) : undefined}>
      <TableHeader>
        <TableRow>
          {columns.map((col, i) => (
            <TableHead key={i} className={col.align === 'right' ? 'text-right' : undefined}>
              {col.headerTip ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      tabIndex={0}
                      className="cursor-help underline decoration-dotted decoration-muted-foreground/40 underline-offset-2 hover:decoration-muted-foreground"
                    >
                      {col.header}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">{col.headerTip}</TooltipContent>
                </Tooltip>
              ) : (
                col.header
              )}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length ? (
          rows.map((row, r) => (
            <TableRow key={r}>
              {columns.map((col, ci) => (
                <TableCell
                  key={ci}
                  className={col.align === 'right' ? 'text-right tabular-nums' : undefined}
                >
                  {col.cell(row)}
                </TableCell>
              ))}
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
              {emptyText}
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
